import { Maybe } from '@Types'
import { v4 as uuidv4 } from 'uuid'
import { OverclaimDocumentStatus } from '@Entities/overclaim/overclaim-uploaded-document'

export interface DocumentToUpload {
    file: File
    description?: Maybe<string>
}

export type PresignedDocumentUpload = { memberUploadId: number } & (
    | { presignedUrl: string; reused?: false }
    | {
          presignedUrl: null
          reused: true
          originalUploadedAt: string
          originalDocumentStatus?: Maybe<OverclaimDocumentStatus>
      }
)

export interface DuplicateDocument {
    name: string
    originalUploadedAt: string
    originalDocumentStatus?: Maybe<OverclaimDocumentStatus>
}

export interface UploadedDocument {
    memberUploadId: number
    name: string
    size: number
    description?: Maybe<string>
    lastModified: string
}

export interface ExistingSelectedDocument {
    memberUploadId: number
    name: string
    size: number
    description?: Maybe<string>
    lastModified?: string
}

export class UnifiedDocument {
    private readonly _docToUpload: DocumentToUpload | null
    private readonly _uploadedDocument: UploadedDocument | null
    private readonly _existingSelectedDocument: ExistingSelectedDocument | null
    private readonly _isComplete: boolean
    private readonly _clientId: string

    constructor(
        docToUpload: DocumentToUpload | UploadedDocument | ExistingSelectedDocument,
        isComplete = false,
        clientId = uuidv4()
    ) {
        this._docToUpload = null
        this._uploadedDocument = null
        this._existingSelectedDocument = null
        this._isComplete = isComplete
        this._clientId = clientId
        if ('file' in docToUpload) {
            this._docToUpload = docToUpload
        } else if ('memberUploadId' in docToUpload) {
            this._existingSelectedDocument = docToUpload
        } else {
            this._uploadedDocument = docToUpload
        }
    }

    get clientId() {
        return this._clientId
    }

    get name() {
        return (
            this._docToUpload?.file.name ??
            this._uploadedDocument?.name ??
            this._existingSelectedDocument?.name ??
            '(unnamed)'
        )
    }

    get size() {
        return this._docToUpload?.file.size ?? this._uploadedDocument?.size ?? this._existingSelectedDocument?.size ?? 0
    }

    get description() {
        return (
            this._docToUpload?.description ??
            this._uploadedDocument?.description ??
            this._existingSelectedDocument?.description ??
            undefined
        )
    }

    get isComplete() {
        return this._isComplete ?? false
    }

    get hasFile() {
        return !!this._docToUpload?.file
    }

    get isExistingSelection() {
        return !!this._existingSelectedDocument
    }

    get existingMemberUploadId() {
        return this._existingSelectedDocument?.memberUploadId
    }

    get file(): File {
        if (this._docToUpload && this._docToUpload.file) {
            return this._docToUpload.file
        } else {
            throw new Error('No file available in UploadedDocument')
        }
    }

    get key(): string {
        return (
            this._docToUpload?.file.name ??
            this._uploadedDocument?.memberUploadId ??
            this._existingSelectedDocument?.memberUploadId ??
            'unknown'
        ).toString()
    }

    get lastModified() {
        return this._uploadedDocument?.lastModified
            ? new Date(this._uploadedDocument?.lastModified).getTime()
            : this._existingSelectedDocument?.lastModified
              ? new Date(this._existingSelectedDocument.lastModified).getTime()
              : (this._docToUpload?.file.lastModified ?? 0)
    }

    static withComplete(doc: UnifiedDocument) {
        return new UnifiedDocument(
            doc._docToUpload ?? doc._uploadedDocument ?? doc._existingSelectedDocument!,
            true,
            doc.clientId
        )
    }

    static withDescription(doc: UnifiedDocument, description: string) {
        if (doc._docToUpload) {
            return new UnifiedDocument({ ...doc._docToUpload, description }, doc._isComplete, doc.clientId)
        } else if (doc._existingSelectedDocument) {
            return new UnifiedDocument({ ...doc._existingSelectedDocument, description }, doc._isComplete, doc.clientId)
        } else {
            throw new Error('Cannot add description to an UploadedDocument')
        }
    }

    static fromUpload(file: DocumentToUpload) {
        return new UnifiedDocument(file)
    }

    static fromApi(file: UploadedDocument) {
        return new UnifiedDocument(file, true)
    }

    static fromExistingSelection(file: ExistingSelectedDocument) {
        return new UnifiedDocument(file)
    }
}
